'use client';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { PATH_URL } from '@/constants/path-constants';
import { fetchOnboardingStepsQueryCreator } from '@/query/fetchOnboardingStepsQueryCreator';
import type {
  NEW_ONBOARDING_FLOW,
  NEW_ONBOARDING_STEP,
} from '@/shared/types/welcome';
import { isHandlingGoogleAuthAtom } from '../components/shared/atoms';
import { getOnboardingSuccessRedirectDestination } from '../utils/utils';

export function useFetchOnboardingSteps({
  onboardingStep,
}: {
  onboardingStep: NEW_ONBOARDING_STEP;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const newOnboardingFlow = searchParams.get('new_onboarding');
  const isHandlingGoogleAuth = useAtomValue(isHandlingGoogleAuthAtom);

  const {
    data: onboardingData,
    isLoading: isFetchingOnboardingSteps,
    isError: isErrorFetchingOnboardingSteps,
  } = useQuery(
    fetchOnboardingStepsQueryCreator({
      flow_name: newOnboardingFlow as NEW_ONBOARDING_FLOW,
      additionalOptions: {
        enabled: !!newOnboardingFlow,
      },
    }),
  );

  const canShowCurrentStep = onboardingData?.next_step === onboardingStep;
  const auxiliaryData = onboardingData?.auxiliary_data;

  useEffect(() => {
    if (isErrorFetchingOnboardingSteps) {
      router.push(`/${PATH_URL.HOME}`);
      return;
    }

    if (!onboardingData || isFetchingOnboardingSteps || isHandlingGoogleAuth)
      return;

    if (canShowCurrentStep) return;

    const destination = getOnboardingSuccessRedirectDestination(
      onboardingData,
      searchParams,
    );

    router.push(destination);
  }, [
    onboardingData,
    isFetchingOnboardingSteps,
    isErrorFetchingOnboardingSteps,
    canShowCurrentStep,
    searchParams,
    router,
    isHandlingGoogleAuth,
  ]);

  return {
    auxiliaryData,
    isFetchingOnboardingSteps,
    canShowCurrentStep,
    isErrorFetchingOnboardingSteps,
    newOnboardingFlow,
  };
}
